import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  limit,
  onSnapshot,
  doc,
  setDoc,
  addDoc,
  getDocs,
  deleteDoc,
  Timestamp,
  writeBatch,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Quarter, VTODocument, COLLECTION_NAMES, Rock } from '../types';
import { useAuth } from '../../../contexts/AuthContext';
import { getQuarterEndDate } from './vto.util';

const QUARTER_ORDER: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];

/** Compute the quarter/year that follows the given one (Q4 -> Q1 of the next year). */
function nextQuarterOf(quarter: Quarter, year: number): { quarter: Quarter; year: number } {
  if (quarter === 'Q4') return { quarter: 'Q1', year: year + 1 };
  const idx = QUARTER_ORDER.indexOf(quarter);
  return { quarter: QUARTER_ORDER[idx + 1], year };
}

/**
 * Hook for managing VTO data with Firestore
 * Replaces Supabase queries with Firestore v9+ modular API
 * Consolidates useVTO and useVTOAutoSave functionality
 */
export function useVTO(quarter: Quarter, year: number, departmentId?: string | null) {
  const { currentUser } = useAuth();
  const [vto, setVto] = useState<VTODocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Effect to listen for VTO document changes
  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // Build query constraints with department filter
    const constraints: QueryConstraint[] = [
      where('quarter', '==', quarter),
      where('year', '==', year),
    ];

    // Add department filter
    if (departmentId) {
      constraints.push(where('departmentId', '==', departmentId));
    } else {
      constraints.push(where('departmentId', '==', null));
    }

    constraints.push(limit(1));

    // Firestore query based on quarter, year, and department
    const q = query(collection(db, COLLECTION_NAMES.VTO), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.empty) {
          setVto(null);
        } else {
          const docData = snapshot.docs[0].data();
          setVto({ 
            id: snapshot.docs[0].id, 
            ...docData 
          } as VTODocument);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching VTO:", err);
        setError("Failed to load V/TO document.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [quarter, year, departmentId]);

  // Function to create a new VTO document
  const createVTO = useCallback(async (createDepartmentId?: string | null): Promise<VTODocument | null> => {
    if (!currentUser) {
      setError('Must be logged in to create V/TO');
      return null;
    }

    try {
      setError(null);

      // Use provided departmentId or fall back to current departmentId
      const vtoDepartmentId = createDepartmentId !== undefined ? createDepartmentId : departmentId;

      // Create a new VTO document with default values
      const newVTO: Omit<VTODocument, 'id'> = {
        organizationName: 'Future Home Loans',
        quarter,
        year,
        coreValues: [],
        purpose: '',
        niche: '',
        tenYearTarget: '',
        targetMarket: '',
        targetMarketDetails: { entries: [] },
        threeUniques: ['', '', ''],
        provenProcess: '',
        guarantee: '',
        threeYearDate: '',
        threeYearRevenueTarget: 0,
        threeYearProfitTarget: 0,
        threeYearForecastedLoans: 0,
        threeYearReviewTargetPct: 0,
        threeYearMeasurables: [],
        threeYearDescription: [],
        currentRevenue: 0,
        currentProfit: 0,
        currentFundedLoans: 0,
        currentReviews: 0,
        oneYearDate: '',
        oneYearRevenueTarget: 0,
        oneYearProfitTarget: 0,
        oneYearForecastedLoans: 0,
        oneYearReviewTargetPct: 0,
        oneYearMeasurables: [],
        oneYearGoals: [],
        rocksDate: '',
        rocksRevenueTarget: 0,
        rocksProfitTarget: 0,
        rocksMeasurables: [],
        rocksList: [],
        issuesList: [],
        departmentId: vtoDepartmentId || null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAMES.VTO), newVTO);
      
      // The onSnapshot listener will automatically update the state
      return { id: docRef.id, ...newVTO } as VTODocument;
    } catch (err: any) {
      console.error('Error creating V/TO:', err);
      setError(err.message || 'Failed to create V/TO document.');
      return null;
    }
  }, [quarter, year, departmentId, currentUser]);

  // Function to update/autosave a VTO document
  const updateVTO = useCallback(async (formData: Partial<VTODocument>): Promise<boolean> => {
    if (!vto) {
      setError('No V/TO to update');
      return false;
    }
    
    const vtoRef = doc(db, COLLECTION_NAMES.VTO, vto.id);
    try {
      // Use setDoc with merge to update or create fields
      await setDoc(vtoRef, {
        ...formData,
        updatedAt: Timestamp.now(),
      }, { merge: true });
      return true;
    } catch (err: any) {
      console.error("Error updating VTO:", err);
      setError(err.message || 'Failed to update V/TO document.');
      return false;
    }
  }, [vto]);
  
  // Function to create Rocks from the VTO
  // This logic is migrated from the source CreateRocksFromVTOModal.tsx
  const createRocksFromVTO = useCallback(async (
    rocksToInsert: Array<Omit<Rock, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<void> => {
    if (!rocksToInsert || rocksToInsert.length === 0) {
      throw new Error('No rocks to insert');
    }

    if (!currentUser) {
      throw new Error('Must be logged in to create rocks');
    }

    // Per Directive 3, we MUST use Firestore, not Supabase
    const rocksCollection = collection(db, COLLECTION_NAMES.ROCKS);
    
    try {
      const batch = writeBatch(db); // Use a batch for efficiency
      
      rocksToInsert.forEach(rock => {
        const newRockRef = doc(rocksCollection); // Auto-generate ID
        batch.set(newRockRef, {
          ...rock,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      });
      
      await batch.commit();
    } catch (err: any) {
      console.error("Error creating rocks from VTO:", err);
      throw new Error(err.message || "Failed to create rocks in Firestore.");
    }
  }, [currentUser]);

  // Push the currently-loaded V/TO forward into the next quarter.
  // Returns { status: 'exists' } (without writing) if a target V/TO already exists and
  // overwrite was not requested, so the UI can prompt for confirmation.
  const pushToNextQuarter = useCallback(async (
    source: VTODocument,
    opts?: { overwrite?: boolean }
  ): Promise<{ status: 'created' | 'overwritten' | 'exists'; quarter: Quarter; year: number }> => {
    if (!currentUser) {
      throw new Error('Must be logged in to push a V/TO forward');
    }

    const target = nextQuarterOf(source.quarter, source.year);
    const targetDeptId = source.departmentId || null;

    // Look for an existing V/TO in the target quarter for the same department.
    const existingSnap = await getDocs(
      query(
        collection(db, COLLECTION_NAMES.VTO),
        where('quarter', '==', target.quarter),
        where('year', '==', target.year),
        where('departmentId', '==', targetDeptId)
      )
    );

    if (!existingSnap.empty && !opts?.overwrite) {
      return { status: 'exists', quarter: target.quarter, year: target.year };
    }

    // Full clone of every Vision + Traction field. `?? ...` fallbacks guard against
    // undefined values (which Firestore rejects) from older/partial documents.
    const clone: Omit<VTODocument, 'id'> = {
      organizationName: source.organizationName ?? 'Future Home Loans',
      quarter: target.quarter,
      year: target.year,
      coreValues: source.coreValues ?? [],
      purpose: source.purpose ?? '',
      niche: source.niche ?? '',
      tenYearTarget: source.tenYearTarget ?? '',
      targetMarket: source.targetMarket ?? '',
      targetMarketDetails: source.targetMarketDetails ?? { entries: [] },
      threeUniques: source.threeUniques ?? ['', '', ''],
      provenProcess: source.provenProcess ?? '',
      guarantee: source.guarantee ?? '',
      threeYearDate: source.threeYearDate ?? '',
      threeYearRevenueTarget: source.threeYearRevenueTarget ?? 0,
      threeYearProfitTarget: source.threeYearProfitTarget ?? 0,
      threeYearForecastedLoans: source.threeYearForecastedLoans ?? 0,
      threeYearReviewTargetPct: source.threeYearReviewTargetPct ?? 0,
      threeYearMeasurables: source.threeYearMeasurables ?? [],
      threeYearDescription: source.threeYearDescription ?? [],
      currentRevenue: source.currentRevenue ?? 0,
      currentProfit: source.currentProfit ?? 0,
      currentFundedLoans: source.currentFundedLoans ?? 0,
      currentReviews: source.currentReviews ?? 0,
      oneYearDate: source.oneYearDate ?? '',
      oneYearRevenueTarget: source.oneYearRevenueTarget ?? 0,
      oneYearProfitTarget: source.oneYearProfitTarget ?? 0,
      oneYearForecastedLoans: source.oneYearForecastedLoans ?? 0,
      oneYearReviewTargetPct: source.oneYearReviewTargetPct ?? 0,
      oneYearMeasurables: source.oneYearMeasurables ?? [],
      oneYearGoals: source.oneYearGoals ?? [],
      rocksDate: source.rocksDate ?? '',
      rocksRevenueTarget: source.rocksRevenueTarget ?? 0,
      rocksProfitTarget: source.rocksProfitTarget ?? 0,
      rocksMeasurables: source.rocksMeasurables ?? [],
      rocksList: source.rocksList ?? [],
      issuesList: source.issuesList ?? [],
      departmentId: targetDeptId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: currentUser.uid,
    };

    try {
      const overwrote = !existingSnap.empty;
      // When overwriting, delete the existing target document(s) first, then create fresh.
      if (overwrote) {
        await Promise.all(
          existingSnap.docs.map((d) => deleteDoc(doc(db, COLLECTION_NAMES.VTO, d.id)))
        );
      }
      await addDoc(collection(db, COLLECTION_NAMES.VTO), clone);
      return { status: overwrote ? 'overwritten' : 'created', quarter: target.quarter, year: target.year };
    } catch (err: any) {
      console.error('Error pushing V/TO to next quarter:', err);
      setError(err.message || 'Failed to push V/TO to the next quarter.');
      throw err;
    }
  }, [currentUser]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { 
    vto, 
    loading, 
    error, 
    createVTO, 
    updateVTO, 
    createRocksFromVTO,
    pushToNextQuarter,
    clearError,
  };
}


